import { formatUnits } from "@ethersproject/units";
import { useEthers } from "@usedapp/core";
import useContract, {
  useContractMethods,
  useTokenContract,
} from "../lib/contract/useContract";
import useAppState from "../lib/hooks/useAppState";
import { formatToUSD } from "../lib/number";
import { LoadingButton as MUILoadingButton } from "@mui/lab";
import { useRef, useState, FormEvent } from "react";
import { Drawer, DrawerProps, Portal, Tooltip } from "@mui/material";
import { useUnStakeDialog } from "./UnstakeDialog";
import Countdown, { CountdownRenderProps } from "react-countdown";
import useMedia from "../lib/hooks/useMedia";
import { ClockIcon } from "lucide-react";
import { useSnackbar } from "./Snackbar";
import { CircularProgress } from "@mui/material";
import { Check } from "@mui/icons-material";
import clsx from "clsx";

const USER_INFOS_LABEL_MAP: { [key: string]: string } = {
  stakedAmount: "Staked Amount",
  // rewardDebt: "Reward Debt",
  lastDepositTime: "Last Stake",
  unstakeStartTime: "Time Left to Claim",
  // pendingAmount: "Pending Amount",
};

const IS_BIG_NUMBER = ["stakedAmount"];
const BUY_URL =
  "https://app.uniswap.org/#/swap?outputCurrency=0x00b78238925c320159023c2ac9ef89da8f16d007"; // TODO: change to mars protocol buy link

const Completionist = () => <span>You're eligible to claim now!</span>;

const renderer = ({ completed, formatted }: CountdownRenderProps) => {
  if (completed) {
    // Render a completed state
    return <Completionist />;
  } else {
    // Render a countdown
    return (
      <div className="font-bold text-base">
        {formatted.days}:{formatted.hours}:{formatted.minutes}:
        {formatted.seconds}
      </div>
    );
  }
};

const CountdownDrawer = ({ anchor, open, onClose, children }: DrawerProps) => {
  return (
    <Drawer anchor={anchor} open={open} onClose={onClose}>
      {children}
    </Drawer>
  );
};

export default function Stake() {
  const {
    totalStakedAmount,
    isAvailableToClaim,
    claim,
    userInfos,
    unstakeEndTime,
    stakeToken,
    refreshUserInfo,
    approveToken,
  } = useContract();
  const { isConnected } = useAppState();
  const { openUnStakeDialog } = useUnStakeDialog();
  const { account } = useEthers();
  // const { openStakeDialog } = useStakeDialog();
  const tokenContract = useTokenContract();
  const [isClaiming, setIsClaiming] = useState(false);
  const { matchesDesktop } = useMedia();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isStaking, setIsStaking] = useState(false);
  const [step, setStep] = useState(0);
  const currencyInputRef = useRef<HTMLInputElement>(null);
  const { openSnackbar: show } = useSnackbar();

  const { data: balance = "0.0" } = useContractMethods<string>(
    tokenContract,
    "balanceOf",
    [account],
    (result) => formatUnits(result, 18),
    [account]
  );

  const handleStake = async (e: FormEvent) => {
    e.preventDefault();
    const amount = Math.floor(
      Number(currencyInputRef?.current?.value)
    ).toString();
    setIsStaking(true);
    if (Number(amount) > Number(balance)) {
      show("You don't have enough tokens to stake", "error");
      setIsStaking(false);
      return;
    }
    if (Number(amount) < 1) {
      show("Amount cannot be less than 1", "error");
      setIsStaking(false);
      return;
    }
    try {
      setStep(1);
      await approveToken(amount);
      show("Stake allowance amount approved", "success");
      setStep(2);
      await stakeToken(amount);
      show("Staking successful", "success");
      setStep(3);
    } catch (error) {
      show((error as Error)?.message || "Staking failed", "error");
    } finally {
      refreshUserInfo();
      setTimeout(() => {
        setIsStaking(false);
        setStep(0);
        if (currencyInputRef.current) {
          currencyInputRef.current.value = "";
        }
      }, 2000);
    }
  };

  const anchor = useRef(null);

  const handleClaim = async () => {
    if (!account) {
      return;
    }
    setIsClaiming(true);
    await claim();
    setIsClaiming(false);
  };
  const renderInfoValue = (key: string) => {
    if (!userInfos) {
      return;
    }
    if (IS_BIG_NUMBER.includes(key)) {
      if (key === "rewardDebt") {
        return (
          <div className="space-y-0" key={key}>
            <div className="font-bold text-sm md:text-sm text-white">
              {USER_INFOS_LABEL_MAP[key]}
            </div>
            <div className="flex items-center justify-between">
              <div className="text-lg text-secondary min-w-200 font-bold">
                {formatToUSD(formatUnits(userInfos?.[key], 18))} VPS
              </div>
            </div>
          </div>
        );
      }
      if (key === "pendingAmount") {
        return (
          <div className="space-y-0" key={key}>
            <div className="font-bold text-sm md:text-sm text-white">
              {USER_INFOS_LABEL_MAP[key]}
            </div>
            <div className="flex items-center justify-between">
              <div className="text-lg text-secondary min-w-200 font-bold">
                {formatToUSD(formatUnits(userInfos?.[key], 18))} VPS
              </div>
              <div className="flex items-center">
                <Tooltip
                  followCursor={matchesDesktop}
                  title={
                    <>
                      <Countdown
                        zeroPadTime={2}
                        date={unstakeEndTime}
                        renderer={renderer}
                      />
                      <div className="text-sm text-secondary">
                        Time Left to Claim
                      </div>
                    </>
                  }
                >
                  <span
                    ref={anchor}
                    onClick={(e) => {
                      if (!matchesDesktop && !isAvailableToClaim) {
                        setIsDrawerOpen(true);
                      }
                    }}
                  >
                    <MUILoadingButton
                      variant="outlined"
                      size="small"
                      onClick={handleClaim}
                      loading={isClaiming}
                      loadingIndicator={"Loading..."}
                      disabled={
                        userInfos.pendingAmount.isZero() || !isAvailableToClaim
                      }
                    >
                      Claim
                    </MUILoadingButton>
                  </span>
                </Tooltip>
              </div>
            </div>
            {!isAvailableToClaim && !matchesDesktop && (
              <Portal>
                <CountdownDrawer
                  open={isDrawerOpen}
                  onClose={() => setIsDrawerOpen(false)}
                  anchor={"bottom"}
                >
                  <div className="p-4 flex flex-col items-center rounded-t-lg">
                    <Countdown
                      zeroPadTime={2}
                      date={unstakeEndTime}
                      renderer={renderer}
                    />
                    <div className="text-sm text-secondary">
                      Time Left to Claim
                    </div>
                  </div>
                </CountdownDrawer>
              </Portal>
            )}
          </div>
        );
      }
      if (key === "stakedAmount") {
        return (
          <div
            className="space-y-0 bg-backgroundTertiary border border-border rounded-lg p-4"
            key={key}
          >
            <div className="font-medium text-sm md:text-sm text-white text-opacity-50">
              {USER_INFOS_LABEL_MAP[key]}
            </div>
            <div className="flex items-center justify-between">
              <div className="text-lg text-white font-bold">
                {formatToUSD(formatUnits(userInfos?.[key], 18))} VPS
              </div>
              {/* <div className="flex flex-col items-center space-y-1">
                <MUILoadingButton
                  disabled={userInfos.stakedAmount.isZero()}
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={openUnStakeDialog}
                >
                  Unstake
                </MUILoadingButton>
              </div> */}
            </div>
          </div>
        );
      }
      return (
        <div className="space-y-0" key={key}>
          <div className="font-bold text-sm md:text-sm text-white">
            {USER_INFOS_LABEL_MAP[key]}
          </div>
          <div className="text-lg text-secondary min-w-200 font-bold">
            {formatToUSD(formatUnits(userInfos?.[key], 18))} VPS
          </div>
        </div>
      );
    }
    if (
      key === "lastDepositTime" &&
      totalStakedAmount &&
      Number(totalStakedAmount) > 0
    ) {
      return (
        <div className="flex items-center gap-1 sm:col-span-2" key={key}>
          <ClockIcon className="w-4 h-4 text-secondary" />
          <div className="font-medium text-sm md:text-sm text-secondary">
            {USER_INFOS_LABEL_MAP[key]}:
          </div>
          <time className="text-sm text-secondary">
            {new Date(
              Number(formatUnits(userInfos.lastDepositTime, 0)) * 1000
            ).toLocaleString()}
          </time>
        </div>
      );
    }
    if (key === "unstakeStartTime") {
      return null;
    }
    return null;
  };

  return (
    <>
      <div className="flex flex-col w-full border-b border-border sm:px-8 px-4 py-4">
        {isConnected && (
          <div className="space-y-1 flex-1">
            <div className="font-medium text-secondary text-md">
              Total Staked
            </div>
            <div className="text-xl md:text-xl text-white min-w-200 font-bold">
              {totalStakedAmount ? formatToUSD(totalStakedAmount) : 0} VPS
            </div>
          </div>
        )}
      </div>
      {isConnected && account && (
        <div className="flex flex-col w-full space-y-4 sm:px-8 px-4 rounded-md">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-medium text-md text-secondary">
                Your Info ({account?.substr(0, 4)}...
                {account?.substr(account?.length - 4, 4)})
              </div>
              <button
                className="text-sm text-background bg-primary px-6 py-2 rounded-md"
                onClick={() => {
                  typeof window !== "undefined" &&
                    window.open(BUY_URL, "_blank");
                }}
              >
                Buy
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
              <div className="space-y-0 bg-backgroundTertiary border border-border rounded-lg p-4">
                <div className="font-medium text-sm md:text-sm text-white text-opacity-50">
                  Your Balance
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-lg text-white font-bold">
                    {balance ? formatToUSD(balance) : 0} VPS
                  </div>
                </div>
              </div>

              {userInfos ? (
                <>
                  {Object.keys(userInfos || {}).map(
                    (key) =>
                      Object.keys(USER_INFOS_LABEL_MAP).includes(key) &&
                      renderInfoValue(key)
                  )}
                </>
              ) : (
                <div className="text-gray-600 min-w-200">
                  You don't have any stake yet.
                </div>
              )}
            </div>

            <form onSubmit={handleStake} className="flex flex-col space-y-2">
              <div className="flex flex-col flex-1">
                <div className="text-sm font-bold text-secondary">
                  Enter Amount
                </div>
                <div className="flex items-center space-x-2 relative">
                  <input
                    disabled={isStaking}
                    name="amount"
                    placeholder="0.0"
                    type="string"
                    ref={currencyInputRef}
                    className="relative z-0 w-full bg-backgroundTertiary border border-border rounded-md p-2 text-white focus:outline-none"
                  />
                  <button
                    type="button"
                    className="text-primary uppercase absolute right-4 text-sm"
                    onClick={() => {
                      if (currencyInputRef.current) {
                        currencyInputRef.current.value = (
                          Number(balance) > 0 ? Number(balance) : 0
                        ).toString();
                      }
                    }}
                  >
                    Max
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm text-secondary">
                <div>Your VPS balance</div>
                <div>{formatToUSD(balance)} VPS</div>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 text-sm text-background bg-primary px-6 py-4 rounded-md disabled:opacity-50"
                  disabled={
                    isStaking || Number(currencyInputRef?.current?.value) < 0
                  }
                >
                  {isStaking ? "Staking..." : "Stake"}
                </button>

                <button
                  type="button"
                  className="flex-1 text-sm text-white bg-backgroundTertiary px-6 py-4 rounded-md"
                  disabled={
                    !userInfos?.stakedAmount || userInfos.stakedAmount.isZero()
                  }
                  onClick={openUnStakeDialog}
                >
                  Unstake
                </button>
              </div>

              {isStaking && (
                <div className="text-base text-secondary">
                  <div className="flex flex-col space-y-2 pt-2">
                    <div className="flex items-center space-x-2">
                      <div
                        className={clsx(
                          "h-6 w-6 rounded-full flex items-center justify-center",
                          {
                            "bg-primary text-white": step === 0,
                            "bg-secondary text-white": step >= 3,
                          }
                        )}
                      >
                        {step < 2 && step !== 0 && (
                          <CircularProgress size="16px" color="inherit" />
                        )}
                        {step >= 2 ? (
                          <Check fontSize="small" color="inherit" />
                        ) : (
                          <div className="h-2 w-2" />
                        )}
                      </div>
                      <div className="font-bold">
                        {step < 2 && step !== 0 && "Approving Transaction"}
                        {step >= 2 && "Transaction Approved"}
                        {step === 0 && "Transaction Approval"}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div
                        className={clsx(
                          "h-6 w-6 flex rounded-full items-center justify-center",
                          {
                            "bg-white text-white": step === 0,
                            "bg-green-500 text-white": step >= 3,
                          }
                        )}
                      >
                        {step < 3 && step !== 0 && (
                          <CircularProgress size="16px" color="inherit" />
                        )}
                        {step >= 3 ? (
                          <Check fontSize="small" color="inherit" />
                        ) : (
                          <div className="h-2 w-2" />
                        )}
                      </div>
                      <div className="font-bold">
                        {step < 3 && step !== 0 && "Approving Staking"}
                        {step >= 3 && "Staking Approved"}
                        {step === 0 && "Staking Approval"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
}
